import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetScreeningConfigByCaseTypeQuery } from '@/services/api/screeningConfigService';

import { getCaseTypeApiValue } from '../../constants';
import {
  ScreeningAnswer,
  ScreeningAnswers,
  ScreeningFormData,
  ScreeningTextFields,
} from '../types';
import { useConfigDrivenQualification } from './useConfigDrivenQualification';
import { useQualificationEngine } from './useQualificationEngine';

interface UseScreeningStateProps {
  initialClientName?: string;
  initialFirmId?: string;
  initialEmail?: string;
  initialPhoneNumber?: string;
  initialCaseType?: string;
  initialLossDate?: string;
  initialState?: string;
  initialAnswers?: ScreeningAnswers;
  initialTextFields?: ScreeningTextFields;
  initialNotes?: string;
  initialIntakerId?: string;
  initialIntakerName?: string;
}

export const useScreeningState = ({
  initialClientName = '',
  initialFirmId = '',
  initialEmail = '',
  initialPhoneNumber = '',
  initialCaseType = '',
  initialLossDate = '',
  initialState = '',
  initialAnswers = {},
  initialTextFields = {},
  initialNotes = '',
  initialIntakerId = '',
  initialIntakerName = '',
}: UseScreeningStateProps = {}) => {
  // Track if user has made any edits (to avoid overwriting user changes with stale props)
  const hasUserEditedRef = useRef(false);
  // Track what we last synced to detect actual prop changes
  const lastSyncedPropsRef = useRef<string>('');

  // Form data state
  const [formData, setFormData] = useState<ScreeningFormData>({
    clientName: initialClientName,
    email: initialEmail,
    phoneNumber: initialPhoneNumber,
    firmId: initialFirmId,
    caseType: initialCaseType,
    lossDate: initialLossDate,
    location: initialState,
    intakerId: initialIntakerId,
    intakerName: initialIntakerName,
  });

  // Screening answers state
  const [answers, setAnswers] = useState<ScreeningAnswers>(initialAnswers);

  // Text field data for sub-questions (e.g., police report, insurance info)
  const [textFields, setTextFields] =
    useState<ScreeningTextFields>(initialTextFields);

  // Incident description (scratchpad)
  const [incidentDescription, setIncidentDescription] = useState(initialNotes);

  // Sync initial values when they change (e.g., after API data loads or page refresh)
  // Only sync if:
  // 1. User hasn't made edits yet, OR
  // 2. Props have actually changed (not just a re-render)
  useEffect(() => {
    // Create a fingerprint of current props to detect real changes
    const propsFingerprint = JSON.stringify({
      answers: initialAnswers,
      textFields: initialTextFields,
      notes: initialNotes,
      caseType: initialCaseType,
      lossDate: initialLossDate,
      state: initialState,
      clientName: initialClientName,
      email: initialEmail,
      phoneNumber: initialPhoneNumber,
    });

    const hasInitialData =
      Object.keys(initialAnswers).length > 0 ||
      initialNotes ||
      initialCaseType ||
      initialLossDate ||
      initialState;

    // Skip if no data to sync
    if (!hasInitialData) {
      return;
    }

    // Skip if props haven't actually changed
    if (propsFingerprint === lastSyncedPropsRef.current) {
      return;
    }

    // Skip if user has made edits (don't overwrite their changes).
    // Intake switching is handled by the parent remounting ScreeningSheet
    // (via key={intakeId}), so we don't need to reset the flag here.
    // Resetting on any prop change was causing cursor jumps: after auto-save
    // the refetch updates props, which was incorrectly treated as an intake
    // switch, allowing stale server data to overwrite the user's draft.
    if (hasUserEditedRef.current) {
      lastSyncedPropsRef.current = propsFingerprint;
      return;
    }

    // Sync from props
    lastSyncedPropsRef.current = propsFingerprint;
    setAnswers(initialAnswers);
    setTextFields(initialTextFields);
    setIncidentDescription(initialNotes);
    setFormData((prev) => ({
      ...prev,
      clientName: initialClientName || prev.clientName,
      email: initialEmail || prev.email,
      phoneNumber: initialPhoneNumber || prev.phoneNumber,
      caseType: initialCaseType || prev.caseType,
      lossDate: initialLossDate || prev.lossDate,
      location: initialState || prev.location,
    }));
  }, [
    initialAnswers,
    initialTextFields,
    initialNotes,
    initialCaseType,
    initialLossDate,
    initialState,
    initialClientName,
    initialEmail,
    initialPhoneNumber,
  ]);

  // Count answered questions
  const answeredCount = useMemo(() => {
    return Object.values(answers).filter((v) => v !== 'unknown' && v).length;
  }, [answers]);

  // Extract state abbreviation from location (handles both "CA" and "California, CA" formats)
  const stateAbbrev = useMemo(
    () => formData.location.split(',').pop()?.trim() || '',
    [formData.location],
  );

  // Fetch screening config for the current case type (for config-driven qualification)
  const apiCaseType = getCaseTypeApiValue(formData.caseType);
  const { data: screeningConfig } = useGetScreeningConfigByCaseTypeQuery(
    { case_type: apiCaseType || '', firm_id: formData.firmId || undefined },
    { skip: !apiCaseType },
  );

  // Calculate qualification status using config-driven rules when config is available
  const configDrivenResult = useConfigDrivenQualification(
    formData.firmId,
    answers,
    formData.caseType,
    stateAbbrev,
    formData.lossDate,
    screeningConfig,
  );

  // Fallback to legacy engine when no config is available
  const legacyResult = useQualificationEngine(
    formData.firmId,
    answers,
    formData.caseType,
    stateAbbrev,
    formData.lossDate,
  );

  // Prefer config-driven qualification when config is available
  const qualificationResult = screeningConfig
    ? configDrivenResult
    : legacyResult;

  // Update form field (marks as user-edited)
  const updateFormField = useCallback(
    <K extends keyof ScreeningFormData>(
      field: K,
      value: ScreeningFormData[K],
    ) => {
      hasUserEditedRef.current = true;
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Update answer for a question (marks as user-edited)
  const updateAnswer = useCallback(
    (questionId: string, answer: ScreeningAnswer) => {
      hasUserEditedRef.current = true;
      setAnswers((prev) => ({ ...prev, [questionId]: answer }));
    },
    [],
  );

  // Update text field for a sub-question (marks as user-edited)
  const updateTextField = useCallback(
    (questionId: string, fieldLabel: string, value: string) => {
      hasUserEditedRef.current = true;
      setTextFields((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          [fieldLabel]: value,
        },
      }));
    },
    [],
  );

  // Update incident description (marks as user-edited)
  const updateIncidentDescription = useCallback((description: string) => {
    hasUserEditedRef.current = true;
    setIncidentDescription(description);
  }, []);

  // Check if a question should show sub-questions
  const shouldShowSubQuestions = useCallback(
    (parentQuestionId: string) => {
      return answers[parentQuestionId] === 'yes';
    },
    [answers],
  );

  // Check if a backup question should be shown
  const shouldShowBackupQuestion = useCallback(
    (parentQuestionId: string) => {
      return answers[parentQuestionId] === 'no';
    },
    [answers],
  );

  // Reset all state
  const resetState = useCallback(() => {
    setFormData({
      clientName: initialClientName,
      email: initialEmail,
      phoneNumber: initialPhoneNumber,
      firmId: initialFirmId,
      caseType: '',
      lossDate: '',
      location: '',
      intakerId: initialIntakerId,
      intakerName: initialIntakerName,
    });
    setAnswers({});
    setTextFields({});
    setIncidentDescription('');
  }, [
    initialClientName,
    initialEmail,
    initialPhoneNumber,
    initialFirmId,
    initialIntakerId,
    initialIntakerName,
  ]);

  return {
    // State
    formData,
    answers,
    textFields,
    incidentDescription,
    qualificationResult,
    answeredCount,

    // Actions
    updateFormField,
    updateAnswer,
    updateTextField,
    setIncidentDescription: updateIncidentDescription,
    shouldShowSubQuestions,
    shouldShowBackupQuestion,
    resetState,
  };
};

export type UseScreeningStateReturn = ReturnType<typeof useScreeningState>;
